import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import "../pages/StaffList.css";
import { exportStaff } from "../utils/exportStaff";

const isOfficeStaff = member => (Array.isArray(member.role) ? member.role : [member.role])
  .some(role => String(role || "").trim().toLowerCase() === "office");

function StaffList() {
  // State to store staff data
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch staff from Firestore
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "staff"));
        const staffList = querySnapshot.docs.map((doc) => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            availability: Array.isArray(docData.availability)
              ? docData.availability
              : docData.availability
              ? [docData.availability] // Convert single value to array
              : [] // Default to empty array if missing
          };
        });

        const visibleStaff = staffList.filter(member => (Array.isArray(member.role) ? member.role : [member.role])
          .some(role => String(role ?? "").trim().length > 0)
          && member.availability.some(day => String(day ?? "").trim().length > 0));

        // Sort staff alphabetically by name
        visibleStaff.sort((a, b) => a.name.localeCompare(b.name));
        setStaff(visibleStaff);
        setFilteredStaff(visibleStaff);
      } catch (error) {
        console.error("Error fetching staff:", error);
      }
    };

    fetchStaff();
  }, []);

  // Handle search filtering
  useEffect(() => {
    const filtered = staff.filter((member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStaff(filtered);
  }, [searchTerm, staff]);

  // Handle staff selection
  const handleStaffClick = (staffMember) => {
    setSelectedStaff(staffMember);
  };

  return (
    <div className="staff-list-container">
      {/* Left Panel - Staff List & Export */}
      <div className="left-panel">
        <h2>Staff Members</h2>
        <input
          type="text"
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {/* Export Button */}
        <button className="export-button" onClick={() => exportStaff(staff)}>
          Export PDF
        </button>
        {/* Render the filtered staff list */}
        {[{label:"Photographers",office:false},{label:"Office staff",office:true}].map(group=>{
          const members=filteredStaff.filter(member=>isOfficeStaff(member)===group.office);
          return <section className="staff-category" key={group.label} aria-label={group.label}>
            <h3>{group.label} <span>({members.length})</span></h3>
            <ul className="staff-list">{members.map(member=><li key={member.id} className="staff-item" onClick={()=>handleStaffClick(member)}>{member.name}</li>)}</ul>
            {!members.length && <p className="staff-category-empty">{searchTerm ? "No matching staff." : "No staff in this category."}</p>}
          </section>;
        })}
      </div>

      {/* Right Panel - Staff Details */}
      <div className="right-panel">
        {selectedStaff ? (
          <div className="staff-details">
            <h2>{selectedStaff.name}</h2>

            <div className="section-divider">
              <h3>Nickname:</h3>
              <p>{selectedStaff.nickname}</p>
            </div>

            <div className="section-divider">
              <h3>City:</h3>
              <p>{selectedStaff.city}</p>
            </div>

            <div className="section-divider">
              <h3>Phone:</h3>
              <p>{selectedStaff.phone || "No Phone Provided"}</p>
            </div>

            <div className="section-divider">
              <h3>Email:</h3>
              <p>{selectedStaff.email}</p>
            </div>

            <div className="section-divider">
              <h3>Roles:</h3>
              <p>{selectedStaff.role}</p>
            </div>

            <div className="section-divider">
              <h3>Availability:</h3>
              {Array.isArray(selectedStaff.availability) ? (
                <ul className="availability-list">
                  {selectedStaff.availability.map((day, index) => (
                    <li key={index}>{day}</li>
                  ))}
                </ul>
              ) : selectedStaff.availability ? (
                <p>{selectedStaff.availability.toString()}</p>
              ) : (
                <p>No Availability Provided</p>
              )}
            </div>
          </div>
        ) : (
          <h2>Select a staff member to view details</h2>
        )}
      </div>
    </div>
  );
}

export default StaffList;
